import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { loadModules } from "esri-loader";
import proj4 from "proj4";

import { GISConfig } from "../models/gis-config.model";
import { GisTokenService } from "../../gis/services/gis-token.service";


@Injectable()
export class GisLocationService {
    config: GISConfig;

    latitude = new BehaviorSubject<number>(0.0);
    latitude$ = this.latitude.asObservable();
    longitude = new BehaviorSubject<number>(0.0);
    longitude$ = this.longitude.asObservable();
    zoom = new BehaviorSubject<number>(15);
    zoom$ = this.zoom.asObservable();
    statePlaneY = new BehaviorSubject<number>(0.0);
    statePlaneY$ = this.statePlaneY.asObservable();
    center: Array<number> = [this.longitude.getValue(), this.latitude.getValue()];
    statePlaneX = new BehaviorSubject<number>(0.0);
    statePlaneX$ = this.statePlaneX.asObservable();

    constructor(
        public readonly tokenService: GisTokenService
    ) {
        this.config = window["GIS.Conf"] as GISConfig;
    }

    updateLatitude(data: number, coordinateSystem: number = CoordinateSystem.OHIO_SOUTH) {
        if (data && this.latitude.getValue() !== data) {
            // Make sure this IS lat not Y
            if (data < 180 && data > -180) {
                this.latitude.next(data);
            } else {
                switch (coordinateSystem) {
                    case CoordinateSystem.OHIO_SOUTH:
                        let t1 = this.statePlaneSouthToWebMercator(this.statePlaneY.getValue(), data)[1];
                        this.latitude.next(this.statePlaneSouthToWebMercator(this.statePlaneY.getValue(), data)[0]);
                        break;
                    case CoordinateSystem.OHIO_NORTH:
                        let t2 = this.statePlaneNorthToWebMercator(this.longitude.getValue(), data)[1];
                        this.latitude.next(this.statePlaneNorthToWebMercator(this.longitude.getValue(), data)[0]);
                        break;
                }
            }
        }
    }

    updateLongitude(data: number, coordinateSystem: number = CoordinateSystem.OHIO_SOUTH) {
        if (data && this.longitude.getValue() !== data) {
            // Make sure this is long not X
            if (data < 180 && data > -180) {
                this.longitude.next(data);
            } else {
                switch (coordinateSystem) {
                    case CoordinateSystem.OHIO_SOUTH:
                        this.longitude.next(this.statePlaneSouthToWebMercator(data, this.statePlaneX.getValue())[1]);
                        break;
                    case CoordinateSystem.OHIO_NORTH:
                        this.longitude.next(this.statePlaneNorthToWebMercator(data, this.statePlaneX.getValue())[1]);
                        break;
                }
            }
        }
    }

    updateZoom(data: number) {
        if (this.zoom.getValue() !== data) {
            this.zoom.next(data);
        }
    }

    updatestatePlaneX(data: number, coordinateSystem: number = CoordinateSystem.OHIO_SOUTH) {
        if (data && this.statePlaneX.getValue() !== data) {
            // This is X - not longitude
            this.statePlaneX.next(data);
        }
    }

    updatestatePlaneY(data: number, coordinateSystem: number = CoordinateSystem.OHIO_SOUTH) {
        if (data && this.statePlaneY.getValue() !== data)
            this.statePlaneY.next(data);
    }

    async lngLatToXY(lng: number = 0, lat: number = 0) {
        try {
            const [webMercatorUtils] = await loadModules([
                "esri/geometry/support/webMercatorUtils"
            ]);
            let latLng = webMercatorUtils.lngLatToXY(lng, lat);

            return latLng;
        }
        catch (error) {
            console.log(`Error converting longitude/latitude to X/Y: ${error}`);
        }
    }

    webMercatorToStatePlaneSouth(lat: number = 0, lng: number = 0): number[] {
        // THESE VALUES RETRIEVED FROM http://spatialreference.org/ref/ 
        const firstProjection = 'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84", 6378137, 298.257223563,AUTHORITY["EPSG", "7030"]],TOWGS84[0, 0, 0, 0, 0, 0, 0],AUTHORITY["EPSG", "6326"]],PRIMEM["Greenwich", 0,AUTHORITY["EPSG", "8901"]],UNIT["degree", 0.0174532925199433,AUTHORITY["EPSG", "9108"]],AUTHORITY["EPSG", "4326"]]';
        const secondProjection = 'PROJCS["NAD_1983_StatePlane_Ohio_South_FIPS_3402_Feet",GEOGCS["GCS_North_American_1983",DATUM["D_North_American_1983",SPHEROID["GRS_1980", 6378137.0, 298.257222101]],PRIMEM["Greenwich", 0.0],UNIT["Degree", 0.0174532925199433]],PROJECTION["Lambert_Conformal_Conic"],PARAMETER["False_Easting", 1968500.0],PARAMETER["False_Northing", 0.0],PARAMETER["Central_Meridian", -82.5],PARAMETER["Standard_Parallel_1", 38.73333333333333],PARAMETER["Standard_Parallel_2", 40.03333333333333],PARAMETER["Latitude_Of_Origin", 38.0],UNIT["Foot_US", 0.3048006096012192]]';
        let latLng = proj4(firstProjection, secondProjection, [lat, lng]);

        return latLng;
    }

    statePlaneSouthToWebMercator(x: number = 0, y: number = 0, updateObservables: boolean = false): number[] {
        // THESE VALUES RETRIEVED FROM http://spatialreference.org/ref/ 
        // Ohio State plane north - WKID 3735
        const firstProjection = '+proj=lcc +lat_1=40.03333333333333 +lat_2=38.73333333333333 +lat_0=38 +lon_0=-82.5 +x_0=600000 +y_0=0 +ellps=GRS80 +datum=NAD83 +to_meter=0.3048006096012192 +no_defs';
        // Web mercator -  EPSG 3857
        const secondProjection = '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs';
        // Web mercator decimal
        const temp = proj4(firstProjection, secondProjection, [x, y]);
        const latLng = this.webMercatorToLatLng(temp[0], temp[1], secondProjection);
       
        latLng[0] = parseFloat(latLng[0].toFixed(6));
        latLng[1] = parseFloat(latLng[1].toFixed(6));
        // Update Observables?
        if (updateObservables) {
            this.updateLatitude(latLng[0]);
            this.updateLongitude(latLng[1]);
        }

        return latLng;
    }

    webMercatorToStatePlaneNorth(x: number = 0, y: number = 0): number[] {
        // THESE VALUES RETRIEVED FROM http://spatialreference.org/ref/ 
        //const firstProjection = 'GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84", 6378137, 298.257223563,AUTHORITY["EPSG", "7030"]],TOWGS84[0, 0, 0, 0, 0, 0, 0],AUTHORITY["EPSG", "6326"]],PRIMEM["Greenwich", 0,AUTHORITY["EPSG", "8901"]],UNIT["degree", 0.0174532925199433,AUTHORITY["EPSG", "9108"]],AUTHORITY["EPSG", "4326"]]';
        const firstProjection = '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs';
        // Ohio State Plane NOrth
        const secondProjection = '+proj=lcc +lat_1=40.03333333333333 +lat_2=38.73333333333333 +lat_0=38 +lon_0=-82.5 +x_0=600000 +y_0=0 +ellps=GRS80 +datum=NAD83 +to_meter=0.3048006096012192 +no_defs';
        // Convert lat Long to webmercator decimal
        let decimal = this.latLongToWebMercator(x, y, firstProjection);
        let statePlane = proj4(firstProjection, secondProjection, [decimal[0], decimal[1]]);

        return statePlane;
    }

    statePlaneNorthToWebMercator(x: number = 0, y: number = 0, updateObservables: boolean = false): number[] {
        // THESE VALUES RETRIEVED FROM http://spatialreference.org/ref/ 
        // Ohio State plane north - WKID 3735
        const firstProjection = '+proj=lcc +lat_1=41.7 +lat_2=40.43333333333333 +lat_0=39.66666666666666 +lon_0=-82.5 +x_0=600000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=us-ft +no_defs';
        // Web mercator -  EPSG 3857
        const secondProjection = '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs';
        // Web mercator decimal
        const temp = proj4(firstProjection, secondProjection, [x, y]);
        const latLng = this.webMercatorToLatLng(temp[0], temp[1], secondProjection);
        latLng[0] = parseFloat(latLng[0].toFixed(6));
        latLng[1] = parseFloat(latLng[1].toFixed(6));
        // Update Observables?
        if (updateObservables) {
            this.updateLatitude(latLng[0]);
            this.updateLongitude(latLng[1]);
        }

        return latLng;
    }

    statePlaneToWebMercator(x: number = 0, y: number = 0, statePlane: number = 0, updateObservables: boolean = false) {
        let result: number[];
        switch (statePlane) {
            case CoordinateSystem.OHIO_SOUTH:
                result = this.statePlaneSouthToWebMercator(x, y, updateObservables);
                break;
            case CoordinateSystem.OHIO_NORTH:
                result = this.statePlaneNorthToWebMercator(x, y, updateObservables);
                break;
        }
        return result;
    }

    webMercatorToLatLng(x: number, y: number, inProjection: string): number[] {
        const latLngProjection = '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs ';

        return proj4(inProjection, latLngProjection, [x, y]).reverse();
    }

    latLongToWebMercator(x: number, y: number, outProjection: string): number[] {
        const latLngProjection = '+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs ';

        return proj4(latLngProjection, outProjection, [x, y]);
    }

    async xYToLngLat(X: number = 0, Y: number = 0) {
        try {
            const [webMercatorUtils] = await loadModules([
                "esri/geometry/support/webMercatorUtils"
            ]);

            return webMercatorUtils.xyToLngLat(X, Y);
        }
        catch (error) {
            console.log(`Error converting X/Y to longitude/latitude: ${error}`);
        }
    }

    async getCounty(x: number, y: number): Promise<string> {
        let error;
        try {
            return this.tokenService.getToken().then(async response => {
                const [Query, QueryTask, Point] = await loadModules([
                    "esri/tasks/support/Query",
                    "esri/tasks/QueryTask",
                    "esri/geometry/Point"]);
                const featureLayerUrl = `${this.config.countiesGISLayer}/?f=json&token=${response}`;
                const testQueryTask = new QueryTask({
                    url: featureLayerUrl
                });
                const testPoint = new Point({ latitude: y, longitude: x });
                const query = new Query();
                query.geometry = testPoint;
                query.spatialRelationship = "intersects";
                query.returnGeometry = false;
                query.outFields = ["*"];
                return testQueryTask.execute(query).then(result => {
                    if (result && result.features && result.features.length > 0) {
                        const county = result.features.find(f => f.attributes["COUNTY"] !== "");
                        if (county) return county.attributes["COUNTY"]
                    }
                    return "";
                })
            });
        }
        catch (error) {
            console.log(`Error in locationService.getCounty: ${error}`);
        }
    }

    // In the Process of Migrating these queries to the gis-query service
    async getCountyAndTownshipByLatLng(x: number, y: number): Promise<string> {
        var error;
        try {
            return this.tokenService.getToken().then(async response => {
                const [Query, QueryTask, Point] = await loadModules([
                    "esri/tasks/support/Query",
                    "esri/tasks/QueryTask",
                    "esri/geometry/Point"
                ]);

                const featureLayerUrl = this.config.townshipGISLayer + "/" + "?f=json&token=" + response;
                const testQueryTask = new QueryTask({
                    url: featureLayerUrl
                });

                const testPoint = new Point({
                    latitude: y,
                    longitude: x
                });
                const query = new Query();
                query.geometry = testPoint;
                query.spatialRelationship = "intersects";
                //query.distance = 100;
                //query.units = "miles";
                query.returnGeometry = false;
                query.outFields = ["*"];
                //query.where = "1=1";
                return testQueryTask.execute(query).then(result => {
                    if (result && result.features && result.features.length > 0 && result.features[0].attributes) {
                        return {
                            Township: result.features[0].attributes["TOWNSHIP"] || "",
                            County: result.features[0].attributes["COUNTY"] || "",
                            Latitude: this.latitude.getValue(),
                            Longitude: this.longitude.getValue()
                        }
                    };
                });
            });
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async getLatLngFromStatePlane(x: number, y: number, updateObservables: boolean = false): Promise<number[]> {
        const latLng = this.statePlaneNorthToWebMercator(x, y);
        if (updateObservables) {
            this.updateLatitude(latLng[0]);
            this.updateLongitude(latLng[1]);
        }

        return latLng;
    }

    // In the Process of Migrating these queries to the gis-query service
    async getPopulationByCoordinate(x: number, y: number): Promise<string> {
        var error;
        try {
            return this.tokenService.getToken().then(async response => {
                const [Query, QueryTask, Point] = await loadModules([
                    "esri/tasks/support/Query",
                    "esri/tasks/QueryTask",
                    "esri/geometry/Point"
                ]);

                let featureLayerUrl = this.config.populationGISLayer + "/" + "?f=json&token=" + response;
                let testQueryTask = new QueryTask({
                    url: featureLayerUrl
                });
                const testPoint = new Point({
                    latitude: y,
                    longitude: x
                });
                const query = new Query();
                query.geometry = testPoint;
                query.spatialRelationship = "intersects";
                query.returnGeometry = false;
                query.outFields = ["*"];
                return testQueryTask.execute(query).then(result => {
                    if (result.features.length) {
						let population = result.features[0].attributes["POPULATION"];
						if (population == null) {
							population = result.features[0].attributes["POP_2020"]
						}
						return {
							Population: population,
							ZoneType: result.features[0].attributes["TYPE"]
						};
                    } else {
                        return {
                            Population: "NONURB",
                            ZoneType: ""
                        };
                    }
                }, reject => { return { Population: "ERROR", ZoneType: "ERROR" }; });
            });
        } catch (error) {
            console.log(`We have an error: ${error}`);
        }
    }

    async getState(x: number, y: number): Promise<string> {
        try {
            return this.tokenService.getToken().then(async response => {
                const [Query, QueryTask, Point] = await loadModules([
                    "esri/tasks/support/Query",
                    "esri/tasks/QueryTask",
                    "esri/geometry/Point"
                ]);
                const featureLayerUrl = `${this.config.statesGISLayer}/?f=json&token=${response}`;
                const testQueryTask = new QueryTask({
                    url: featureLayerUrl
                });
                const testPoint = new Point({ latitude: y, longitude: x });
                const query = new Query();
                query.geometry = testPoint;
                query.spatialRelationship = "intersects";
                query.returnGeometry = false;
                query.outFields = ["*"];
                return testQueryTask.execute(query).then(result => {
                    if (result && result.features && result.features.length > 0) {
                        const state = result.features.find(f => f.attributes["STATE"] !== "");
                        if (state) return state.attributes["STATE"];
                    }
                    return "";
                }, (reject) => { return ""; })
            });
        } catch (error) {
            console.log(`Error in locationService.getState: ${error}`);
        }
    }
}

export enum CoordinateSystem {
    OHIO_NORTH = 0,
    OHIO_SOUTH = 1
}